import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import addAccessibilityModule from "highcharts/modules/accessibility";
import Highcharts from "highcharts";

import { HighChart } from "../../../resusableComponents/common/formElements";
import { Panel } from "../../../resusableComponents/funnelFuel";

import { readChartColors } from "../lib/chartColors";
import { fmt } from "../lib/selectors";

// Keyboard navigation and screen-reader descriptions for the chart. Registers on the shared Highcharts instance.
addAccessibilityModule(Highcharts);

const CHART_HEIGHT = 260;

// Whole leads per week; a rate that does not apply (e.g. nothing left to close) is a dash, not a 0.
const rate = (value) => ((value === null || value === undefined) ? "—" : fmt(Math.round(value)));

const Kpi = ({ label, value, note, warn }) => (
  <div className="ff-kpi">
    <div className="ff-lab">{label}</div>
    <div className="ff-val" data-warn={warn || undefined}>{value}</div>
    <div className="ff-note">{note}</div>
  </div>
);

// Says so instead of rendering an empty box when the charting library throws.
class ChartBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  render() {
    if (this.state.failed) {
      return (
        <div className="ff-pace-chart ff-chart-failed" style={{ minHeight: CHART_HEIGHT }}>
          <span className="ff-state-msg" role="status">{this.props.message}</span>
        </div>
      );
    }

    return this.props.children;
  }
}

const buildChartOptions = (intl, pace, series, active, colors) => ({
  chart: { height: CHART_HEIGHT, backgroundColor: "transparent", spacing: [8, 4, 4, 0], animation: false },
  title: { text: null },
  credits: { enabled: false },
  accessibility: {
    enabled: true,
    description: intl.formatMessage({ id: "ff_dashboard.pace.chart_description", defaultMessage: "Cumulative leads delivered against the campaign target" })
  },
  legend: { align: "left", itemStyle: { fontSize: "12px", color: colors.legend, fontWeight: "400" } },
  xAxis: {
    min: 0, max: pace.totalDays, tickInterval: 14,
    lineColor: colors.axis, tickColor: colors.axis,
    labels: { format: `${intl.formatMessage({ id: "ff_dashboard.pace.day", defaultMessage: "Day" })} {value}`, style: { color: colors.label, fontSize: "11px" } },
    plotLines: [{
      value: pace.elapsedDays, color: colors.marker, width: 1, dashStyle: "Dot", zIndex: 3,
      label: { text: intl.formatMessage({ id: "ff_dashboard.pace.today", defaultMessage: "Today" }), style: { color: colors.label, fontSize: "11px" } }
    }]
  },
  yAxis: {
    min: 0, max: Math.max(pace.booked, 1), title: { text: null }, gridLineColor: colors.grid,
    labels: { style: { color: colors.label, fontSize: "11px" } }
  },
  tooltip: {
    shared: true,
    headerFormat: `<b>${intl.formatMessage({ id: "ff_dashboard.pace.day", defaultMessage: "Day" })} {point.key}</b><br>`
  },
  plotOptions: { series: { animation: false, states: { inactive: { opacity: 1 } } } },
  // Three fixed series, created once. A filter only changes data, the comparison line's visibility and one name.
  series: [
    {
      name: intl.formatMessage({ id: "ff_dashboard.pace.series_target", defaultMessage: "Target" }),
      type: "line", color: colors.target, dashStyle: "Dash", lineWidth: 2,
      marker: { enabled: false }, enableMouseTracking: false, data: series.target
    },
    {
      name: intl.formatMessage({ id: "ff_dashboard.pace.series_all", defaultMessage: "All delivered" }),
      type: "line", color: colors.all, lineWidth: 2, marker: { enabled: false },
      visible: active, showInLegend: active, data: series.all
    },
    {
      name: intl.formatMessage({
        id: active ? "ff_dashboard.pace.series_selection" : "ff_dashboard.pace.series_delivered",
        defaultMessage: active ? "Selection" : "Delivered"
      }),
      type: "area", color: colors.selection, fillOpacity: 0.14, lineWidth: 2.5, marker: { enabled: false }, data: series.selection
    }
  ]
});

/*
  The chart is created ONCE (stable options object) and then only updated through setData with animation off.
  Recreating it costs 40-60 ms per filter click; setData costs almost nothing. The reference chart is not
  clickable (tooltip only), so this one is not either.
*/
const PaceChart = ({ pace, series, active }) => {
  const intl = useIntl();
  const chartRef = useRef(null);
  const boxRef = useRef(null);
  // Built once, after the container is on the page, because its colours come from the stylesheet (lib/chartColors.js).
  const [options, setOptions] = useState(null);

  useEffect(() => {
    setOptions(buildChartOptions(intl, pace, series, active, readChartColors(boxRef.current)));
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !chart.series || chart.series.length < 3) { return; }

    const [target, all, selection] = chart.series;
    target.setData(series.target, false, false, false);
    all.setData(series.all, false, false, false);
    all.update({ showInLegend: active }, false);
    all.setVisible(active, false);
    selection.setData(series.selection, false, false, false);
    selection.update({
      name: intl.formatMessage({
        id: active ? "ff_dashboard.pace.series_selection" : "ff_dashboard.pace.series_delivered",
        defaultMessage: active ? "Selection" : "Delivered"
      })
    }, false);
    chart.redraw(false);
  }, [series, active]);

  // The chart does not resize on its own when the layout changes without the window changing (e.g. the sidebar).
  useEffect(() => {
    if (!boxRef.current || typeof ResizeObserver === "undefined") { return undefined; }

    let timer;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (chartRef.current) { chartRef.current.reflow(); } }, 120);
    });
    observer.observe(boxRef.current);

    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);

  return (
    <div className="ff-pace-chart" ref={boxRef} style={{ minHeight: CHART_HEIGHT }}>
      {options && <HighChart options={options} getChartRef={(chart) => { chartRef.current = chart; }} />}
    </div>
  );
};

/* Pace against target: the only panel that needs a charting library. */
export const PacePanel = ({ model, view, active }) => {
  const intl = useIntl();
  const { pace, campaign } = model;
  const t = (id, defaultMessage, values) => intl.formatMessage({ id: `ff_dashboard.pace.${id}`, defaultMessage }, values);

  const behind = pace.behindPlan > 0;

  // Totals must reconcile: say how many delivered leads are not on the chart, and why. Counted from the leads
  // themselves; if the server reports more in a category, its figure wins.
  const missing = Math.max(pace.missingDateCount, pace.unplotted.missing);
  const before = Math.max(pace.beforeStartCount, pace.unplotted.before);
  const after = Math.max(pace.afterEndCount, pace.unplotted.after);
  const future = Math.max(pace.futureDatedCount, pace.unplotted.future);
  const unplottable = Math.max(pace.unplottableCount, missing + before + after + future);

  const reasons = [
    missing > 0 && t("caveat_missing", "{count} without an activity date", { count: fmt(missing) }),
    before > 0 && t("caveat_before", "{count} before the start date", { count: fmt(before) }),
    after > 0 && t("caveat_after", "{count} after the end date", { count: fmt(after) }),
    future > 0 && t("caveat_future", "{count} dated in the future", { count: fmt(future) })
  ].filter(Boolean);

  return (
    <Panel
      title={t("title", "Pace against target")}
      actions={(
        <span className="ff-badge" data-ok={!behind || undefined}>
          {behind ? t("behind", "{count} leads behind plan", { count: fmt(pace.behindPlan) }) : t("on_plan", "On or ahead of plan")}
        </span>
      )}
    >
      <div className="ff-pace-layout">
        <ChartBoundary message={t("chart_unavailable", "Chart library did not load.")}>
          <PaceChart pace={pace} series={view.pace} active={active} />
        </ChartBoundary>

        <div className="ff-pace-side">
          <div className="ff-kpis">
            {/* Order matters: Booked before Delivered. */}
            <Kpi label={t("booked", "BOOKED")} value={fmt(pace.booked)} note={t("booked_note", "{count} line items", { count: campaign.lineItemCount })} />
            <Kpi
              label={t("delivered", "DELIVERED")}
              value={fmt(view.pace.delivered)}
              note={active
                ? t("delivered_note_filtered", "of {total} campaign total", { total: fmt(view.totalAll) })
                : t("delivered_note", "{percent}% of booked", { percent: Math.round(pace.percentOfBooked) })}
            />
            <Kpi label={t("rate_needed", "RATE NEEDED")} value={rate(pace.requiredRatePerWeek)} note={pace.requiredRatePerWeek === null && pace.daysRemaining === 0
              ? t("rate_needed_note_ended", "campaign has ended")
              : t("rate_needed_note", "leads per week to close")} warn />
            <Kpi label={t("current_rate", "CURRENT RATE")} value={rate(pace.currentRatePerWeek)} note={t("current_rate_note", "leads per week")} />
          </div>
        </div>
      </div>

      {reasons.length > 0 && (
        <p className="ff-caveat">
          {t("caveat", "{count} delivered leads are not plotted on the chart", { count: unplottable })}
          {": "}{reasons.join(", ")}.
        </p>
      )}
    </Panel>
  );
};
